import { isNumeric } from './number';
import { currencyString } from './string';

const MAX_DECIMAL_PLACES = 2;

export const roundToTwoDecimals = (amount: number): number => {
  return Math.round(amount * 100) / 100;
};

const roundDownTensPlace = (amount: number): number => Math.floor(amount / 10) * 10;

// currencyString always prefixes "$"; strip it for contexts that render their own symbol (e.g. an input prefix).
export const currencyStringWithoutSymbol = (amount: number, withoutCents = false): string => (
  currencyString(amount, withoutCents).replace('$', '')
);

// Lightly sanitizes in-progress currency input (invalid characters, extra decimal points, cents beyond 2 digits)
// without grouping the integer part, so the field stays freely editable until it's reformatted on blur.
export const sanitizeCurrencyInput = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDotIndex = cleaned.indexOf('.');
  const hasDecimal = firstDotIndex !== -1;
  const integerDigits = (hasDecimal ? cleaned.slice(0, firstDotIndex) : cleaned).replace(/\D/g, '');
  const decimalDigits = hasDecimal
    ? cleaned.slice(firstDotIndex + 1).replace(/\D/g, '').slice(0, MAX_DECIMAL_PLACES)
    : '';

  return hasDecimal ? `${integerDigits}.${decimalDigits}` : integerDigits;
};

export const parseCurrencyAmount = (value: string): number | undefined => {
  const numericString = value.replace(/,/g, '');

  return isNumeric(numericString) ? roundToTwoDecimals(Number(numericString)) : undefined;
};

export type CalculateSuggestedDrawAmountsPropsT = {
  maxAmount: number;
  minAmount: number;
};
const MID_SUGGESTED_FACTOR = 0.75;
const MIN_SUGGESTED_FACTOR = 0.5;
const BUFFER = 50;

/** Returns the suggested draw amounts sorted min to max, or an empty array when none should be shown. */
export const calculateSuggestedDrawAmounts = (
  { maxAmount, minAmount }: CalculateSuggestedDrawAmountsPropsT,
): number[] => {
  const maxSuggestedAmount = roundDownTensPlace(maxAmount);
  const midSuggestedAmount = roundDownTensPlace(maxAmount * MID_SUGGESTED_FACTOR);
  let minSuggestedAmount = roundDownTensPlace(maxAmount * MIN_SUGGESTED_FACTOR);

  // There are scenarios where the minimum suggested amount is less than the
  // minimum draw amount, but the maximum amount is still large enough that
  // we want to show the suggested buttons. For example, MN customers have a
  // minimum amount of 1450, but we still want to show the suggested buttons
  // if the maximum amount is 2000.
  if (minSuggestedAmount < minAmount) {
    // If the mid suggested amount is at least 50 more than the minimum amount,
    // we still want to show the buttons as that's a meaningful difference.
    // Otherwise don't show the buttons.
    if (midSuggestedAmount < minAmount + BUFFER) {
      return [];
    }

    // Since the minimum suggested amount is less than the minimum draw amount,
    // the button will show the minimum amount.
    minSuggestedAmount = minAmount;
  }

  return [
    minSuggestedAmount,
    midSuggestedAmount,
    maxSuggestedAmount,
  ];
};
